import { useState, useEffect, useRef, useMemo } from "react";
import { Search, X, Clock, TrendingUp, MapPin, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useTrips } from "@/hooks/useTrips";

interface SearchBarProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

interface Suggestion {
  type: "destination" | "title" | "category" | "recent";
  text: string;
  icon?: React.ReactNode;
}

const SearchBar = ({
  searchTerm,
  setSearchTerm,
}: SearchBarProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: trips } = useTrips();

  // Get recent searches from localStorage
  const getRecentSearches = (): string[] => {
    if (typeof window === "undefined") return [];
    const recent = localStorage.getItem("recentSearches");
    return recent ? JSON.parse(recent) : [];
  };

  // Save search to recent searches
  const saveRecentSearch = (term: string) => {
    if (!term.trim()) return;
    const recent = getRecentSearches();
    const updated = [term, ...recent.filter((s) => s !== term)].slice(0, 5);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  // Generate suggestions based on search term and trips
  const generateSuggestions = useMemo(() => {
    if (!trips) return [];

    const term = searchTerm.toLowerCase().trim();
    const allSuggestions: Suggestion[] = [];

    if (term.length === 0) {
      // Show recent searches and popular destinations when empty
      const recent = getRecentSearches();
      recent.forEach((search) => {
        allSuggestions.push({
          type: "recent",
          text: search,
          icon: <Clock className="w-4 h-4" />,
        });
      });

      // Get popular destinations (most common)
      const destinations = trips.map((t) => t.destination);
      const popularDestinations = Array.from(
        new Set(destinations)
      ).slice(0, 5 - recent.length);
      popularDestinations.forEach((dest) => {
        allSuggestions.push({
          type: "destination",
          text: dest,
          icon: <TrendingUp className="w-4 h-4" />,
        });
      });
    } else {
      // Search in destinations
      const matchingDestinations = Array.from(
        new Set(
          trips
            .filter((t) =>
              t.destination.toLowerCase().includes(term)
            )
            .map((t) => t.destination)
        )
      ).slice(0, 3);

      matchingDestinations.forEach((dest) => {
        allSuggestions.push({
          type: "destination",
          text: dest,
          icon: <MapPin className="w-4 h-4" />,
        });
      });

      // Search in trip titles
      const matchingTitles = trips
        .filter((t) => t.title.toLowerCase().includes(term))
        .slice(0, 3)
        .map((t) => ({
          type: "title" as const,
          text: t.title,
          icon: <Sparkles className="w-4 h-4" />,
        }));

      allSuggestions.push(...matchingTitles);

      // Search in categories
      const matchingCategories = Array.from(
        new Set(
          trips
            .filter((t) => t.category.toLowerCase().includes(term))
            .map((t) => t.category)
        )
      ).slice(0, 2);

      matchingCategories.forEach((cat) => {
        allSuggestions.push({
          type: "category",
          text: cat,
          icon: <Search className="w-4 h-4" />,
        });
      });
    }

    return allSuggestions.slice(0, 8);
  }, [searchTerm, trips]);

  // Update suggestions when search term or trips change
  useEffect(() => {
    setSuggestions(generateSuggestions);
    setSelectedIndex(-1);
  }, [generateSuggestions]);

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSuggestionClick = (text: string) => {
    setSearchTerm(text);
    saveRecentSearch(text);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsFocused(true);
  };

  const handleClear = () => {
    setSearchTerm("");
    setIsFocused(false);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const showSuggestions = isFocused && suggestions.length > 0;

  // Handle keyboard navigation with proper dependencies
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isFocused || suggestions.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault();
        const selectedSuggestion = suggestions[selectedIndex];
        if (selectedSuggestion) {
          handleSuggestionClick(selectedSuggestion.text);
        }
      } else if (e.key === "Escape") {
        setIsFocused(false);
        inputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFocused, suggestions, selectedIndex]);

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div ref={searchRef} className="relative">
            {/* Prominent Search Bar */}
            <div className="relative">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 text-muted-foreground pointer-events-none" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search for Destinations"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleFocus}
                className="pl-16 pr-20 h-16 md:h-20 text-lg md:text-xl bg-white border-2 border-gray-200 rounded-xl shadow-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </Button>
              )}
            </div>

            {/* Autocomplete Suggestions Dropdown */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl overflow-hidden z-50">
                <div className="max-h-96 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.type}-${index}-${suggestion.text}`}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion.text)}
                      className={`w-full px-6 py-4 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        index === selectedIndex ? "bg-primary/10" : ""
                      }`}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <div className="text-muted-foreground">
                        {suggestion.icon}
                      </div>
                      <span className="text-base font-medium text-foreground">
                        {suggestion.text}
                      </span>
                      {suggestion.type === "recent" && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          Recent
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchBar;

